import type { inferRouterOutputs } from "@trpc/server";
import type { WorkspaceRouter } from "replo-publisher/src/trpc/routers/workspace";
import type { WorkspaceRole } from "schemas/generated/workspace";

import * as React from "react";

import { Avatar } from "@replo/design-system/components/avatar/Avatar";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import {
  PopoverContent,
  PopoverRoot,
  PopoverTitle,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replo/design-system/components/table/Table";
import { toast } from "@replo/design-system/components/toast/Toast";
import { skipToken as reactQuerySkipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import Header from "@v2/features/dashboard/Header";
import { InviteTeamMembersModal } from "@v2/features/settings/shop/modals/InviteTeamMembersModal";
import { RemoveMemberModal } from "@v2/features/settings/shop/modals/RemoveMemberModal";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useIsWorkspaceOwner } from "@v2/hooks/useIsWorkspaceOwner";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { projectRouter } from "@v2/trpc/routes/project";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { getProjectName } from "@v2/utils/project-utils";
import {
  ArrowDown,
  ArrowDown10,
  ArrowDownAZ,
  ArrowUp,
  ArrowUp01,
  ArrowUpZA,
  ChevronDown,
  MoreHorizontal,
  Plus,
  Search,
  UserPlus,
} from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

type ProjectMember =
  inferRouterOutputs<WorkspaceRouter>["getWorkspaceAndProjectMembers"][0];

type Field = "member.username" | "member.role" | "member.registeredAt";
type Direction = "asc" | "desc";

type OrderState = {
  label: string;
  icon: React.ReactNode;
  field: Field;
  direction: Direction;
};

const sortOptions: OrderState[] = [
  {
    label: "Last added",
    field: "member.registeredAt",
    direction: "desc",
    icon: <ArrowDown10 />,
  },
  {
    label: "First added",
    field: "member.registeredAt",
    direction: "asc",
    icon: <ArrowUp01 />,
  },
  {
    label: "Name A-Z",
    field: "member.username",
    direction: "asc",
    icon: <ArrowDownAZ />,
  },
  {
    label: "Name Z-A",
    field: "member.username",
    direction: "desc",
    icon: <ArrowUpZA />,
  },
  {
    label: "Most Permissions",
    field: "member.role",
    direction: "desc",
    icon: <ArrowDown />,
  },
  {
    label: "Least Permissions",
    field: "member.role",
    direction: "asc",
    icon: <ArrowUp />,
  },
];

const MemberMenu: React.FC<{
  member: ProjectMember;
  currentUser: { id: string; email: string };
  workspaceId: string;
  currentUserIsOwner: boolean;
  numRegisteredOwners: number;
  onRemoveMember: (member: ProjectMember) => void;
  onResendInvite: (args: { email: string; workspaceId: string }) => void;
  onUpdatePermissions: (
    workspaceId: string,
    email: string,
    role: WorkspaceRole,
  ) => void;
  onInviteMember: (email: string) => void;
}> = ({
  member,
  currentUser,
  workspaceId,
  currentUserIsOwner,
  numRegisteredOwners,
  onRemoveMember,
  onResendInvite,
  onUpdatePermissions,
  onInviteMember,
}) => {
  const role = member.workspaceMemberships[0]?.role as
    | WorkspaceRole
    | undefined;
  const isOnlyRegisteredOwner =
    currentUserIsOwner &&
    numRegisteredOwners === 1 &&
    currentUser.email === member.email;

  const showRemove = !isOnlyRegisteredOwner;
  const showResend = !member.registeredAt;
  const showUpdatePermissions =
    currentUserIsOwner && currentUser.id !== member.id && Boolean(role);
  const showInvite =
    currentUserIsOwner && currentUser.id !== member.id && !role;

  if (!showRemove && !showResend && !showUpdatePermissions && !showInvite) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger>
        <Button icon variant="tertiary" size="sm">
          <MoreHorizontal className="text-muted" />
        </Button>
      </MenuTrigger>
      <MenuContent align="end">
        {showRemove && (
          <MenuItem
            label={`Remove ${member.firstName ?? member.email}`}
            onClick={() => onRemoveMember(member)}
          />
        )}
        {showResend && (
          <MenuItem
            label={`Resend invitation to ${member.email}`}
            onClick={() => {
              onResendInvite({ email: member.email, workspaceId });
            }}
          />
        )}
        {showUpdatePermissions && role && (
          <MenuItem
            label={
              role === "member" ? "Upgrade to Owner" : "Downgrade to Member"
            }
            onClick={() => onUpdatePermissions(workspaceId, member.email, role)}
          />
        )}
        {showInvite && (
          <MenuItem
            label="Invite to Workspace"
            onClick={() => onInviteMember(member.email)}
          />
        )}
      </MenuContent>
    </Menu>
  );
};

const Members: React.FC = () => {
  const { user: currentUser } = useCurrentUser();

  const [searchQueryInputValue, searchQuery, setSearchQueryInputValue] =
    useDebouncedState("");
  const [inviteModalOpen, setInviteModalOpen] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState<string | undefined>(
    undefined,
  );
  const [memberToRemove, setMemberToRemove] =
    React.useState<ProjectMember | null>(null);
  const [order, setOrder] = React.useState<OrderState>({
    label: "Most Permissions",
    field: "member.role",
    direction: "desc",
    icon: <ArrowDown />,
  });

  const { workspace, isLoading: isLoadingWorkspace } =
    useCurrentWorkspaceContext();
  const workspaceId = workspace?.id;

  const { data: members, isPending: isLoadingMembers } =
    workspaceRouter.getWorkspaceAndProjectMembers.useQuery(
      workspaceId ?? reactQuerySkipToken,
    );
  const { data: projects } = projectRouter.listWithStats.useQuery({});

  const logEvent = useLogAnalytics();
  const currentUserIsOwner = useIsWorkspaceOwner(workspaceId);

  const { mutate: resendInvite } = workspaceRouter.resendInvite.useMutation({
    onSuccess: (_, variables) => {
      toast.success({
        title: "Invites sent",
        description: "The invites were sent successfully.",
      });
      logEvent("workspace.invite.resend", {
        resendInviteTo: variables?.email ?? "",
        workspaceId: variables?.workspaceId ?? "",
      });
    },
  });

  const { mutate: updatePermissions } =
    workspaceRouter.updatePermission.useMutation({
      onSuccess: () => {
        if (workspaceId) {
          void workspaceUtils.getWorkspaceAndProjectMembers.invalidate(
            workspaceId,
          );
        }
      },
    });

  const handleUpdatePermissions = async (
    workspaceId: string,
    email: string,
    currentRole: WorkspaceRole,
  ) => {
    const isUserMember = currentRole === "member";
    const newRole = isUserMember ? "owner" : "member";
    void updatePermissions({
      email,
      workspaceId,
      role: newRole,
    });
  };

  const filteredMembers = members?.filter((member) => {
    const query = searchQuery.toLowerCase();
    const fullName = [member.firstName, member.lastName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const email = member.email.toLowerCase();

    return fullName.includes(query) || email.includes(query);
  });

  const sortedProjectMembers = React.useMemo(() => {
    return (
      filteredMembers &&
      sortMembers(order.field, order.direction, filteredMembers).map(
        (member) => ({ ...member, key: member.id }),
      )
    );
  }, [filteredMembers, order.field, order.direction]);

  const numRegisteredOwners =
    members?.filter((member) => {
      return (
        member.workspaceMemberships.length > 0 &&
        member.workspaceMemberships[0]?.role === "owner" &&
        member.registeredAt
      );
    }).length ?? 0;

  const getEmptyState = () => {
    if (searchQuery) {
      return (
        <Empty
          icon={<Search />}
          title="No members found"
          description="Try adjusting your search filters"
          size="base"
        />
      );
    }

    return (
      <Empty
        icon={<UserPlus />}
        title="No members yet"
        description="Invite team members to collaborate on this workspace"
        size="base"
      >
        <Button
          variant="primary"
          size="base"
          startEnhancer={<Plus />}
          onClick={() => {
            if (workspaceId) {
              setInviteEmail(undefined);
              setInviteModalOpen(true);
            }
          }}
        >
          Add Members
        </Button>
      </Empty>
    );
  };

  return (
    <div className="flex w-full flex-col gap-4 px-6 pb-14">
      <Header
        title="Workspace Members"
        size="sm"
        endEnhancer={
          <Button
            variant="primary"
            size="base"
            startEnhancer={<Plus />}
            onClick={() => {
              if (workspaceId) {
                setInviteEmail(undefined);
                setInviteModalOpen(true);
              }
            }}
          >
            Add Members
          </Button>
        }
        isLoading={isLoadingMembers ?? isLoadingWorkspace}
      />
      <div className="flex items-center gap-2 mb-4 flex-shrink-0">
        <Input
          size="base"
          placeholder="Search by name or email"
          type="text"
          value={searchQueryInputValue}
          onChange={(event) => setSearchQueryInputValue(event.target.value)}
          startEnhancer={<Search className="text-muted" />}
          UNSAFE_className="basis-4/5"
        />
        <Menu>
          <MenuTrigger UNSAFE_className="basis-1/5">
            <Button
              variant="secondary"
              size="base"
              endEnhancer={<ChevronDown />}
              startEnhancer={
                React.isValidElement(order.icon) ? order.icon : undefined
              }
              className="w-full"
              UNSAFE_className="justify-between"
            >
              {order.label}
            </Button>
          </MenuTrigger>
          <MenuContent align="end">
            <MenuRadioGroup
              value={String(sortOptions.indexOf(order))}
              onValueChange={(value) => {
                const option = sortOptions[Number(value)];
                if (option) {
                  setOrder(option);
                }
              }}
            >
              {sortOptions.map((option, index) => (
                <MenuRadioItem
                  key={index}
                  value={String(index)}
                  label={option.label}
                  startEnhancer={option.icon}
                />
              ))}
            </MenuRadioGroup>
          </MenuContent>
        </Menu>
      </div>
      <div className="border border-border border-0.5 rounded-xl overflow-hidden">
        <Table UNSAFE_className="table-fixed">
          <TableHeader>
            <TableRow UNSAFE_className="bg-sheet hover:bg-sheet">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>{null}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingMembers &&
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 4 }).map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!isLoadingMembers &&
              (!sortedProjectMembers || sortedProjectMembers.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div className="text-default [&_.border-border]:border-border">
                      {getEmptyState()}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            {!isLoadingMembers &&
              sortedProjectMembers &&
              sortedProjectMembers.length > 0 &&
              sortedProjectMembers.map((member) => (
                <TableRow key={member.key}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={member.firstName}
                        className="h-8 w-8"
                        imageClassName="bg-sky-soft text-primary text-xs font-semibold"
                      />
                      <div className="flex flex-col overflow-hidden">
                        {member.firstName ? (
                          <p className="text-default truncate text-sm">
                            {member.firstName} {member.lastName}
                          </p>
                        ) : (
                          <p className="text-placeholder truncate text-sm">
                            Invited
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-default truncate">{member.email}</p>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const role = member.workspaceMemberships[0]?.role;
                      if (role) {
                        return (
                          <p className="text-default capitalize">{role}</p>
                        );
                      }
                      return (
                        <div>
                          <PopoverRoot>
                            <PopoverTrigger>
                              <div className="text-primary cursor-pointer">
                                Can edit {member.projectMemberships.length} shop
                                {member.projectMemberships.length === 1
                                  ? ""
                                  : "s"}
                              </div>
                            </PopoverTrigger>
                            <PopoverContent
                              showCloseButton={false}
                              UNSAFE_className="w-[300px]"
                            >
                              <PopoverTitle>Shops</PopoverTitle>
                              <div className="max-h-[180px] overflow-scroll text-xs divide-y divide-secondary flex flex-col gap-2">
                                {member.projectMemberships.map(
                                  ({ project }) => {
                                    const fullProject = projects?.projects.find(
                                      (p) => p.id === project.id,
                                    );
                                    return (
                                      <div
                                        key={project.id}
                                        className="text-muted truncate first:pt-0 last:pb-0"
                                      >
                                        {getProjectName(fullProject)}
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            </PopoverContent>
                          </PopoverRoot>
                          <div className="text-muted text-xs">
                            No workspace level access or permissions
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell UNSAFE_className="text-right">
                    {currentUser && workspaceId && (
                      <MemberMenu
                        member={member}
                        currentUser={currentUser}
                        workspaceId={workspaceId}
                        currentUserIsOwner={currentUserIsOwner}
                        numRegisteredOwners={numRegisteredOwners}
                        onRemoveMember={setMemberToRemove}
                        onResendInvite={resendInvite}
                        onUpdatePermissions={(wId, email, role) => {
                          void handleUpdatePermissions(wId, email, role);
                        }}
                        onInviteMember={(email) => {
                          setInviteEmail(email);
                          setInviteModalOpen(true);
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      {workspaceId && (
        <InviteTeamMembersModal
          open={inviteModalOpen}
          onOpenChange={(open) => {
            setInviteModalOpen(open);
            if (!open) {
              setInviteEmail(undefined);
            }
          }}
          workspaceId={workspaceId}
          email={inviteEmail}
        />
      )}
      {memberToRemove && workspaceId && (
        <RemoveMemberModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setMemberToRemove(null);
            }
          }}
          workspaceId={workspaceId}
          user={memberToRemove}
        />
      )}
    </div>
  );
};

const sortMembers = (
  field: Field,
  direction: Direction,
  data: ProjectMember[],
) => {
  const sortedData = [...(data ?? [])];
  sortedData.sort((a, b) => {
    if (field === "member.username" && a.firstName && b.firstName) {
      return direction === "asc"
        ? a.firstName.localeCompare(b.firstName)
        : b.firstName.localeCompare(a.firstName);
    }

    if (field === "member.role") {
      const aRole = (a.workspaceMemberships[0]?.role ?? "none") as
        | WorkspaceRole
        | "none";
      const bRole = (b.workspaceMemberships[0]?.role ?? "none") as
        | WorkspaceRole
        | "none";
      const rolesOrder: Record<
        WorkspaceRole | "none",
        { asc: number; desc: number }
      > = {
        owner: { asc: 1, desc: -1 },
        member: { asc: 0, desc: 0 },
        viewer: { asc: -1, desc: 1 },
        none: { asc: -2, desc: 2 },
      };
      return rolesOrder[aRole][direction] - rolesOrder[bRole][direction];
    }

    if (field === "member.registeredAt" && a.registeredAt && b.registeredAt) {
      return direction === "asc"
        ? a.registeredAt.getTime() - b.registeredAt.getTime()
        : b.registeredAt.getTime() - a.registeredAt.getTime();
    }

    return 0;
  });
  return sortedData;
};

export default Members;
